import { Chip, Divider, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import type { GetApprovalFlowInvoicesListResponse } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { isEmptyArray, isNotEmptyArray, isNotNullOrUndefined } from '@weft/utils';
import { Price } from 'apps/app/src/components/design/Price';
import { useMemo } from 'react';

const NOT_FOUND_INDEX = -1;

type ApprovalFlowHeaderProps = {
  invoiceData?: GetApprovalFlowInvoicesListResponse;
  selectedInvoiceId: string;
  onInvoiceChange?: (invoiceId: string) => void;
};

export const ApprovalFlowHeader: React.FC<ApprovalFlowHeaderProps> = ({ invoiceData, selectedInvoiceId, onInvoiceChange }) => {
  const vyTheme = useTheme();
  const invoicePosition = useMemo(() => invoiceData?.findIndex(invoice => invoice.invoiceId === selectedInvoiceId), [invoiceData, selectedInvoiceId]);
  const selectedInvoice = getSelectedInvoice(invoicePosition, invoiceData);

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (!isNotEmptyArray(invoiceData) || !isValidInvoicePosition(invoicePosition)) {
      return;
    }

    const invoiceToSwitch = direction === 'left' ? invoiceData[invoicePosition - 1] : invoiceData[invoicePosition + 1];

    onInvoiceChange?.(invoiceToSwitch.invoiceId);
  };

  return (
    <Paper
      sx={(theme) => ({
        boxShadow: theme.shadows[1],
      })}
      variant='outlined'
    >
      <Stack
        justifyContent="space-between"
        direction="row"
        padding={2}
      >
        <Stack
          gap={3}
          alignItems="center"
          width="100%"
          direction="row"
        >
          <Chip
            size='medium'
            label={isValidInvoicePosition(invoicePosition) && isNotEmptyArray(invoiceData) 
              ? `${invoicePosition + 1}/${invoiceData.length}` 
              : `0/${invoiceData?.length ?? 0}`}
          />
          <Divider
            flexItem
            orientation='vertical'
          />
          {isNotNullOrUndefined(selectedInvoice) && (
            <>
              <Typography variant='subtitle1'>{`${selectedInvoice.customerName} -- ${selectedInvoice.invoiceName}`}</Typography>
              <Divider
                flexItem
                orientation='vertical'
              />
              <Price
                variant='subtitle1'
                currency={selectedInvoice.currency}
              >
                {selectedInvoice.revenueBreakdown.total}
              </Price>
            </>
          )}
        </Stack>
        <Stack
          gap={1}
          justifyContent="space-between"
          direction="row"
        >
          <IconButton 
            disableRipple
            onClick={() => handleArrowClick('left')}
            disabled={invoicePosition === 0 || !isValidInvoicePosition(invoicePosition)}
            sx={{
              backgroundColor: vyTheme.palette.action.selected,
              borderRadius: 1,
            }}
          >
            <VyIcon
              icon='ArrowSmallLeft'
            />
          </IconButton>
          <IconButton
            disableRipple
            onClick={() => handleArrowClick('right')}
            disabled={isEmptyArray(invoiceData) || invoicePosition === invoiceData.length - 1}
            sx={{
              backgroundColor: vyTheme.palette.action.selected,
              borderRadius: 1,
            }}
          >
            <VyIcon icon='ArrowSmallRight'/>
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

function isValidInvoicePosition(invoicePosition: number | undefined): invoicePosition is number {
  return invoicePosition !== NOT_FOUND_INDEX && isNotNullOrUndefined(invoicePosition);
}

function getSelectedInvoice(invoicePosition: number | undefined, invoiceData: GetApprovalFlowInvoicesListResponse | undefined) {
  return isNotEmptyArray(invoiceData) && isValidInvoicePosition(invoicePosition) 
    ? invoiceData[invoicePosition] 
    : undefined;
}